/**
 * BottomNav — 모바일 전용 하단 네비게이션 (md:hidden)
 * - fixed bottom-0, 높이 64px + safe-area-inset-bottom
 * - 5개 탭: 홈, 탐색, 만들기(+), 내 앨범, 더보기
 * - "만들기" 탭은 BottomSheet 열기 (action type)
 * - "더보기" 탭은 MoreSheet 열기 (sheet type)
 * - 비인증에서도 5탭 모두 표시 (인증 필요 탭은 로그인 리다이렉트)
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { isTabActive } from '../../lib/nav-match';
import { motion } from 'framer-motion';
import { getVisibleNavItems, type NavItem } from '../../config/nav';

interface BottomNavProps {
  onCreateClick: () => void;
  onMoreClick: () => void;
}

const BottomNav: React.FC<BottomNavProps> = ({ onCreateClick, onMoreClick }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const isActive = (path: string | null) => path ? isTabActive(path, location.pathname) : false;

  const handleClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.type === 'action') {
      e.preventDefault();
      if (item.requiresAuth && !isAuthenticated) {
        navigate('/login');
      } else {
        onCreateClick();
      }
      return;
    }
    if (item.type === 'sheet') {
      e.preventDefault();
      onMoreClick();
      return;
    }
    if (item.requiresAuth && !isAuthenticated) {
      e.preventDefault();
      navigate('/login');
    }
  };

  const visibleItems = getVisibleNavItems('bottom');

  return (
    <nav
      id="tour-bottom-nav"
      role="navigation"
      aria-label="하단 네비게이션"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
      style={{ '--bottom-nav-height': 'calc(64px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)' } as React.CSSProperties}
    >
      <div className="flex h-16">
        {visibleItems.map((item, idx) => {
          const active = isActive(item.path);
          const isCreateTab = item.type === 'action';

          if (isCreateTab) {
            return (
              <button
                key={item.labelKey}
                type="button"
                onClick={(e) => handleClick(item, e)}
                className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 min-h-[44px] transition-colors text-gray-500 dark:text-gray-400 hover:text-accent"
                aria-label={t(item.labelKey, item.label)}
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 via-secondary-500 to-accent text-white shadow-md">
                  <item.icon size={22} />
                </span>
              </button>
            );
          }

          const Component = item.path ? Link : 'button';
          const linkProps = item.path ? { to: item.path } : { type: 'button' as const };

          return (
            <Component
              key={item.labelKey}
              {...linkProps}
              onClick={(e: React.MouseEvent) => handleClick(item, e)}
              className={`
                relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5
                min-h-[44px] transition-colors
                ${active
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-accent-dark'
                  : 'text-gray-500 dark:text-gray-400 hover:text-accent hover:bg-gray-50 dark:hover:bg-gray-800'
                }
              `}
              aria-current={active ? 'page' : undefined}
            >
              {active && (
                <motion.div
                  layoutId="bottomNavUnderline"
                  className="absolute top-0 left-0 right-0 h-[2px] bg-accent motion-reduce:transition-none"
                />
              )}
              <span className={active ? 'text-accent-dark' : 'text-gray-500 dark:text-gray-400'}>
                <item.icon size={22} />
              </span>
              <span className={`text-[10px] xs:text-xs font-medium leading-none text-center px-1 ${active ? '' : 'text-gray-500 dark:text-slate-400'}`}>
                {t(item.labelKey, item.label)}
              </span>
            </Component>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
